import { useState } from 'react';
import type { BillingInterval, CreateLaunchRequest } from './types';

interface LaunchCreateFormProps {
  onSubmit: (request: CreateLaunchRequest) => Promise<void>;
  submitting: boolean;
}

export function LaunchCreateForm({ onSubmit, submitting }: LaunchCreateFormProps) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [paymentLink, setPaymentLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || submitting) return;
    setError(null);

    if (!slug.trim() || !name.trim() || !contactEmail.trim()) {
      setError('Clinic slug, clinic name, and contact email are required.');
      return;
    }

    const request: CreateLaunchRequest = {
      slug: slug.trim(),
      name: name.trim(),
      contact_email: contactEmail.trim(),
      billing_interval: billingInterval,
      launch_options: {
        demo: true,
        payment_link: paymentLink,
        public_branding: true,
      },
    };

    setIsSubmitting(true);
    try {
      await onSubmit(request);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <p className="portal-eyebrow">Clinic Demo Creation</p>
          <h3>Create demo clinic</h3>
        </div>
      </div>
      <form className="form" onSubmit={handleSubmit} data-testid="launch-create-form">
        {error && (
          <div className="error-banner" role="alert" data-testid="create-form-error">
            {error}
          </div>
        )}
        <div className="form-row">
          <label htmlFor="clinic-slug">Clinic slug</label>
          <input
            id="clinic-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-clinic"
            data-testid="clinic-slug"
          />
        </div>
        <div className="form-row">
          <label htmlFor="clinic-name">Clinic name</label>
          <input
            id="clinic-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Clinic"
            data-testid="clinic-name"
          />
        </div>
        <div className="form-row">
          <label htmlFor="contact-email">Contact email</label>
          <input
            id="contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="admin@acme.clinic"
            data-testid="contact-email"
          />
        </div>
        <div className="form-row">
          <label htmlFor="billing-interval">Billing plan</label>
          <select
            id="billing-interval"
            value={billingInterval}
            onChange={(e) => {
              setBillingInterval(e.target.value as BillingInterval);
              setPaymentLink(false);
            }}
            disabled={submitting || isSubmitting}
            aria-describedby="billing-plan-description billing-usage-description billing-plan-availability"
            data-testid="billing-interval"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Annual</option>
          </select>
          <p id="billing-plan-description" className="copy-fallback">
            {billingInterval === 'monthly'
              ? 'The $500 monthly base is collected upfront at signup and renews on each monthly payment anniversary. Overage is collected after each UTC calendar month closes.'
              : 'Annual is a 12-month commitment. The $1,350 base is collected upfront at signup and every three months after signup. Four base payments total $5,400. Overage is collected at each calendar quarter end, adding three separate monthly peak overages.'}
          </p>
          <p id="billing-usage-description" className="copy-fallback">
            Includes 100 simultaneously activated pets per UTC calendar month, resetting on the first with still-active pets carried in. Each pet above 100 at the month’s peak costs $7 before tax. Revocation removes access immediately without reducing the recorded peak.
          </p>
          <p id="billing-plan-availability" className="copy-fallback" role="status">
            Existing accepted billing agreements are unchanged.
          </p>
        </div>
        <fieldset className="form-row">
          <legend>Optional payment setup</legend>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={paymentLink}
              onChange={(e) => setPaymentLink(e.target.checked)}
              disabled={submitting || isSubmitting}
              data-testid="launch-option-payment-link"
            />
            Create initial payment link
          </label>
        </fieldset>
        <div className="actions">
          <button className="btn" type="submit" disabled={submitting || isSubmitting} data-testid="create-submit">
            {submitting || isSubmitting ? 'Creating demo clinic…' : 'Create demo clinic'}
          </button>
        </div>
      </form>
    </div>
  );
}
